const DOMAIN_CONTRACTS = Object.freeze([
  {
    bodyKey: "todos",
    resultKey: "todos",
    idKeys: ["clientId", "id"],
    error: "sync_todo_rows_unacknowledged",
  },
  {
    bodyKey: "savedArticles",
    resultKey: "savedArticles",
    idKeys: ["articleId", "id"],
    error: "sync_saved_article_rows_unacknowledged",
  },
]);

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizedId(value) {
  const id = String(value || "").trim();
  return id && id.length <= 2048 ? id : null;
}

function expectedRowReceipt(rows, idKeys) {
  const ids = [];
  const seen = new Set();
  let invalidCount = 0;
  let duplicateCount = 0;
  for (const row of rows) {
    const record = isRecord(row) ? row : {};
    const id = normalizedId(idKeys.map((key) => record[key]).find(Boolean));
    if (!id) {
      invalidCount += 1;
      continue;
    }
    if (seen.has(id)) {
      duplicateCount += 1;
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  return { ids, invalidCount, duplicateCount };
}

function exactIdSet(value, expected) {
  if (!Array.isArray(value) || value.length !== expected.length) return false;
  const normalized = value.map(normalizedId);
  if (normalized.some((id) => !id)) return false;
  const actual = new Set(normalized);
  return (
    actual.size === normalized.length &&
    expected.every((id) => actual.has(id))
  );
}

function domainWasFullyAcknowledged(rows, result, idKeys) {
  if (!isRecord(result)) return false;
  const expected = expectedRowReceipt(rows, idKeys);
  if (expected.invalidCount !== 0 || expected.duplicateCount !== 0) return false;
  return (
    result.fullyAccepted === true &&
    Number(result.receivedCount) === rows.length &&
    Number(result.submittedCount) === expected.ids.length &&
    Number(result.acceptedCount) === expected.ids.length &&
    Number(result.invalidCount) === 0 &&
    Number(result.duplicateCount) === 0 &&
    exactIdSet(result.submittedIds, expected.ids) &&
    exactIdSet(result.acceptedIds, expected.ids)
  );
}

/**
 * A generic HTTP 200 or submitted-row count is not a durable row receipt.
 * Return the first domain whose exact stable IDs were not proven active by
 * the server's post-LWW result.
 */
export function contentRowAcknowledgementFailure(body, response) {
  const request = isRecord(body) ? body : {};
  const results = isRecord(response?.results) ? response.results : {};
  for (const contract of DOMAIN_CONTRACTS) {
    const rows = request[contract.bodyKey];
    if (!Array.isArray(rows)) continue;
    if (
      !domainWasFullyAcknowledged(
        rows,
        results[contract.resultKey],
        contract.idKeys,
      )
    ) {
      return contract.error;
    }
  }
  return null;
}
